<template>
	<div :class="classes">
		<Multiselect
			v-model="selection"
			:id="id"
			ref="multiselect"
			:options="relevantOptions"
			track-by="id"
			label="label"
			:placeholder="placeholder"
			:show-labels="false"
			:close-on-select="closeOnSelect"
			:taggable="false"
			:multiple="multiple"
			:internal-search="!searchType"
			@search-change="onSearchChange"
			@select="onChange"
			@remove="onRemove"
		>
			<template #noResult>{{ noResultsText }}</template>
		</Multiselect>
	</div>
</template>

<script>
import Multiselect from 'vue-multiselect'
import {uuid} from "vue-uuid";
import {get} from "@/helper/characteristic/Label";
import {isEmptyOrNull} from "@/helper/String";
import {isDefined} from "@/helper/Value";
import {isPositiveInteger} from "@/helper/Number";

export const SEARCH_TYPE__POSTAL_CODE_CITY = 'postal-code-city';

export default {

	components: {
		Multiselect,
	},

	props: {
		value: {
			type: [ Array, Object ],
			required: false,
		},
		characteristicItems: {
			type: Array,
			required: false,
		},
		nothingAllowed: {
			type: Object,
			required: false,
		},
		placeholder: {
			type: String,
			required: false,
			default: '- Bitte triff eine Auswahl -'
		},
		customOptions: {
			type: Array,
			required: false,
		},
		multiple: {
			type: Boolean,
			required: false,
			default: false
		},
		labelType: {
			type: String,
			required: false,
		},
		noResultsText: {
			type: String,
			required: false,
			default: 'Nichts gefunden'
		},
		coloring: {
			type: Boolean,
			required: false,
			default: true,
		},
		closeOnSelect: {
			type: Boolean,
			required: false,
			default: true,
		},
		searchType: {
			type: String,
			required: false,
		}
	},

	data ()
	{
		let options;

		let selection = this.value;

		if (this.customOptions)
		{
			options = this.customOptions;
		}
		else
		{
			// set correct label if necessary
			if (this.labelType && selection)
			{
				const selectionAsArray = _.isArray(selection)
					? selection
					: [ selection ];

				_.forEach(selectionAsArray, item =>
				{
					if (item)
					{
						item.label = get(item, this.labelType);
					}
				});
			}

			options = _.cloneDeep(this.characteristicItems);

			_.forEach(options, option =>
			{
				option.label = get(option, this.labelType);
			});

			if (this.nothingAllowed)
			{
				let nothingAllowedOption = {
					id: null,
					label: this.nothingAllowed.text,
				};

				options.unshift(nothingAllowedOption);

				if (
					(this.value && this.value.length === 0)
					|| this.value === null
				)
				{
					selection = [ nothingAllowedOption ];
				}
			}
		}

		return {
			id: uuid.v4(),
			selection,
			options,
			filteredOptions: undefined,
		};
	},

	computed: {
		relevantOptions ()
		{
			return isDefined(this.filteredOptions)
				? this.filteredOptions
				: this.options;
		},
		classes ()
		{
			const mustChoose = this.multiple
				? (this.selection === undefined || (this.selection || []).length === 0)
				: this.selection === undefined;

			return {
				'characteristic-dropdown': true,
				'characteristic-dropdown--must-choose': mustChoose,
				'characteristic-dropdown--coloring': this.coloring,
				'characteristic-dropdown--multiple': this.multiple
			};
		},
		onlyNothingSelected ()
		{
			if (!this.nothingAllowed)
			{
				return false;
			}

			if (this.multiple)
			{
				return this.selection && this.selection.length === 1 && _.first(this.selection).id === null;
			}

			return this.selection?.id === null;
		},
	},

	methods: {
		onSearchChange (keyword)
		{
			if (!this.searchType)
			{
				return;
			}

			if (isEmptyOrNull(keyword))
			{
				this.filteredOptions = undefined;
				return;
			}

			if (this.searchType === SEARCH_TYPE__POSTAL_CODE_CITY)
			{
				keyword = keyword.toLowerCase();

				this.filteredOptions = this.options.filter(option =>
				{
					const label = option.label.toLowerCase();

					if (isPositiveInteger(keyword)) // only numbers, search by "starts with"
					{
						return label.startsWith(keyword.toLowerCase());
					}
					else
					{
						return label.includes(keyword);
					}
				});
			}
		},
		onChange (selectedOption)
		{
			this.$nextTick(() =>
			{
				if (this.nothingAllowed)
				{
					if (selectedOption.id === null)
					{
						this.selection = this.multiple ? [selectedOption] : selectedOption;

						this.$refs.multiselect.deactivate();
					}
					else
					{
						if (this.multiple)
						{
							this.selection = _.filter(this.selection, item => item.id !== null);
						}
					}
				}

				this.emitChangeEvent();
			});
		},
		onRemove ()
		{
			this.$nextTick(() => this.emitChangeEvent());
		},
		emitChangeEvent ()
		{
			if (this.nothingAllowed && this.onlyNothingSelected)
			{
				this.$emit('input', this.multiple ? [] : null);
				return;
			}

			if (!this.multiple)
			{
				this.$emit('input', this.selection ? this.selection : undefined);
				return;
			}

			this.$emit('input', this.selection && this.selection.length > 0 ? this.selection : undefined);
		}
	}
}
</script>
